// src/components/ParticipantDetailDialog.js
import { API_URL } from '../config/apiConfig';
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Avatar,
    Chip,
    Dialog as VideoDialog,
    useMediaQuery,
    useTheme,
    Button,
    DialogActions,
    TextField,
    Grid,
    Autocomplete
} from '@mui/material';
import { Close, PlayCircle, CheckCircle, Edit, Delete } from '@mui/icons-material';
import {
    approveCompetitionResult,
    deleteCompetitionRegistration,
    updateCompetitionRegistration
} from '../api/competitionApi';
import axios from 'axios';


export default function ParticipantDetailDialog({
                                                    open,
                                                    onClose,
                                                    participant,
                                                    competition,
                                                    leaderboard,
                                                    canViewVideos = false,
                                                    onApprovalChange,
                                                    isAdminView = false
                                                }) {
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [selectedResultId, setSelectedResultId] = useState(null);
    const [selectedApproved, setSelectedApproved] = useState(false);
    const [approving, setApproving] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]); // ✅ LISA SEE - hoiame valitud kasutajaid

    // Edit form state
    const [editForm, setEditForm] = useState({
        userId: null,
        teamName: '',
        category: '',
        teamMembers: []
    });

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    if (!participant) return null;

    const getCategoryRank = () => {
        if (!leaderboard || !participant) return 0;
        const categoryLeaderboard = leaderboard.filter(p => p.category === participant.category);
        const sortedCategoryLeaderboard = [...categoryLeaderboard].sort((a, b) => {
            return b.totalPoints - a.totalPoints;
        });
        const rank = sortedCategoryLeaderboard.findIndex(
            p => p.registrationId === participant.registrationId
        ) + 1;
        return rank;
    };

    const overallRank = getCategoryRank();

    const handleVideoClick = (videoUrl, resultId, approved) => {
        setSelectedVideo(videoUrl);
        setSelectedResultId(resultId);
        setSelectedApproved(approved);
    };

    const handleApproveToggle = async () => {
        if (!selectedResultId) {
            console.error('❌ No result ID!');
            return;
        }

        try {
            setApproving(true);
            await approveCompetitionResult(selectedResultId, !selectedApproved);
            setSelectedApproved(!selectedApproved);

            if (onApprovalChange) {
                await onApprovalChange();
            }
        } catch (error) {
            console.error('❌ Error toggling approval:', error);
            alert('Failed to update approval status: ' + error.message);
        } finally {
            setApproving(false);
        }
    };

    const handleOpenEditDialog = async () => {
        // Initialize edit form with current participant data
        if (participant.isTeam) {
            setEditForm({
                teamName: participant.name || '',
                category: participant.category || '',
                teamMembers: participant.teamMemberIds || [] // ✅ Kasuta ID'sid
            });

            // ✅ Laadi tiimi liikmete andmed
            if (participant.teamMemberIds && participant.teamMemberIds.length > 0) {
                try {
                    const token = localStorage.getItem('token');
                    const response = await axios.post(`${API_URL}/users/bulk`, {
                        userIds: participant.teamMemberIds
                    }, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    });
                    setSelectedUsers(response.data); // ✅ Salvesta valitud kasutajad
                    setUserSearchResults(response.data);
                } catch (error) {
                    console.error('Error loading team members:', error);
                }
            }
        } else {
            setEditForm({
                userId: participant.userId,
                category: participant.category || '',
                teamMembers: []
            });

            // ✅ Laadi individuaalse kasutaja andmed
            if (participant.userId) {
                try {
                    const token = localStorage.getItem('token');
                    const response = await axios.get(`${API_URL}/users/${participant.userId}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    });
                    setSelectedUsers([response.data]); // ✅ Salvesta valitud kasutaja
                    setUserSearchResults([response.data]);
                } catch (error) {
                    console.error('Error loading user:', error);
                }
            }
        }
        setOpenEditDialog(true);
    };

    const handleCloseEditDialog = () => {
        setOpenEditDialog(false);
        setEditForm({
            userId: null,
            teamName: '',
            category: '',
            teamMembers: []
        });
        setUserSearchResults([]);
        setSelectedUsers([]); // ✅ LISA SEE
    };

    const searchUsers = async (searchTerm, reason) => {
        // ✅ Ignoreeri reset/clear eventi - hoia valitud kasutajaid
        if (reason === 'reset' || reason === 'clear') {
            setUserSearchResults(selectedUsers);
            return;
        }

        if (!searchTerm || searchTerm.length < 2) {
            // ✅ Kui otsing on tühi, näita ainult valitud kasutajaid
            setUserSearchResults(selectedUsers);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/search-users`, {
                params: { q: searchTerm },
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            // ✅ Kombineeri otsingutulemused valitud kasutajatega (ilma duplikaatideta)
            const searchResults = response.data;
            const selectedIds = selectedUsers.map(u => u.id);
            const newUsers = searchResults.filter(u => !selectedIds.includes(u.id));
            setUserSearchResults([...selectedUsers, ...newUsers]);
        } catch (error) {
            console.error('Error searching users:', error);
        }
    };

    const handleTeamMemberChange = (index, value) => {
        const newTeamMembers = [...editForm.teamMembers];
        newTeamMembers[index] = value?.id || null;
        setEditForm(prev => ({
            ...prev,
            teamMembers: newTeamMembers
        }));

        // ✅ Lisa valitud kasutaja selectedUsers'i (kui pole juba seal)
        if (value && !selectedUsers.find(u => u.id === value.id)) {
            setSelectedUsers(prev => [...prev, value]);
        }
    };

    const handleUpdateRegistration = async () => {
        if (!editForm.category) {
            alert('Please select a category');
            return;
        }

        if (competition.isTeamCompetition) {
            if (!editForm.teamName) {
                alert('Please enter a team name');
                return;
            }
            if (editForm.teamMembers.filter(m => m).length !== competition.teamSize) {
                alert(`Please add exactly ${competition.teamSize} team members`);
                return;
            }
        } else {
            if (!editForm.userId) {
                alert('Please select a participant');
                return;
            }
        }

        try {
            setEditLoading(true);
            await updateCompetitionRegistration(participant.registrationId, {
                userId: competition.isTeamCompetition ? null : editForm.userId,
                teamName: competition.isTeamCompetition ? editForm.teamName : null,
                category: editForm.category,
                teamMembers: competition.isTeamCompetition ? editForm.teamMembers.filter(m => m) : null
            });

            handleCloseEditDialog();
            if (onApprovalChange) {
                await onApprovalChange();
            }
            // Also close the main dialog
            onClose();
        } catch (error) {
            console.error('Error updating registration:', error);
            alert(error.response?.data?.error || 'Failed to update registration');
        } finally {
            setEditLoading(false);
        }
    };

    const handleDeleteRegistration = async () => {
        if (!window.confirm(`Are you sure you want to delete ${participant.name}? This will also delete all their results.`)) {
            return;
        }

        try {
            await deleteCompetitionRegistration(participant.registrationId);
            if (onApprovalChange) {
                await onApprovalChange();
            }
            // Close the dialog after successful deletion
            onClose();
        } catch (error) {
            console.error('Error deleting registration:', error);
            alert('Failed to delete registration: ' + (error.response?.data?.error || error.message));
        }
    };

    const getYoutubeEmbedUrl = (url) => {
        if (!url) return null;
        const videoId = url.split('v=')[1] || url.split('/').pop();
        return `https://www.youtube.com/embed/${videoId}`;
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
                                {participant.name?.charAt(0) || 'P'}
                            </Avatar>
                            <Box>
                                <Typography variant="h6">
                                    {participant.name || `Participant ${participant.userId}`}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {participant.category}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {isAdminView && (
                                <>
                                    <IconButton
                                        onClick={handleOpenEditDialog}
                                        color="primary"
                                        size="small"
                                        title="Edit"
                                    >
                                        <Edit />
                                    </IconButton>
                                    <IconButton
                                        onClick={handleDeleteRegistration}
                                        color="error"
                                        size="small"
                                        title="Delete"
                                    >
                                        <Delete />
                                    </IconButton>
                                </>
                            )}
                            <IconButton onClick={onClose} size="small">
                                <Close />
                            </IconButton>
                        </Box>
                    </Box>
                </DialogTitle>

                <DialogContent dividers sx={{px: { xs: 0, sm: 3 }}}>
                    <Box sx={{ mb: 3, pt: 1 }}>
                        <Chip
                            label={`Category Place: #${overallRank}`}
                            color="primary"
                            sx={{ mr: 1 }}
                        />
                        <Chip
                            label={`Category: ${participant.category}`}
                            sx={{ mr: 1 }}
                        />
                        <Chip
                            label={`Total Points: ${participant.totalPoints}`}
                            color="secondary"
                        />
                    </Box>

                    {participant.isTeam && participant.teamMembers && participant.teamMembers.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                Team Members
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {participant.teamMembers.map((memberName, index) => (
                                    <Chip
                                        key={index}
                                        label={memberName}
                                        size="small"
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                    <Typography variant="h6" gutterBottom>
                        Workout Results
                    </Typography>

                    <TableContainer component={Paper} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Workout</strong></TableCell>
                                    <TableCell><strong>Result</strong></TableCell>
                                    <TableCell><strong>Points</strong></TableCell>
                                    {canViewVideos && (
                                        <TableCell align="center"><strong>Video</strong></TableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {competition.workouts && competition.workouts.map((workout, index) => {
                                    const score = participant.scores[workout.id];
                                    return (
                                        <TableRow key={workout.id}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="bold">
                                                    Workout #{index + 1}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {workout.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {score ? (
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {score.timeCap
                                                                ? `TC - ${score.reps} reps`
                                                                : score.result}
                                                        </Typography>
                                                        {score.tiebreak && workout.tiebreak && (
                                                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                                                Tiebreak: {score.tiebreak}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {score ? (
                                                    <Chip
                                                        label={score.points}
                                                        size="small"
                                                        color="primary"
                                                    />
                                                ) : (
                                                    '-'
                                                )}
                                            </TableCell>
                                            {canViewVideos && (
                                                <TableCell align="center">
                                                    {score?.videoUrl ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                            <IconButton
                                                                color="primary"
                                                                onClick={() => handleVideoClick(
                                                                    score.videoUrl,
                                                                    score.resultId,
                                                                    score.approved
                                                                )}
                                                            >
                                                                <PlayCircle />
                                                            </IconButton>
                                                            {score.approved && (
                                                                <CheckCircle
                                                                    sx={{
                                                                        color: 'success.main',
                                                                        fontSize: 20
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
            </Dialog>

            {/* Edit Registration Dialog */}
            <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">
                            Edit {competition?.isTeamCompetition ? 'Team' : 'Participant'}
                        </Typography>
                        <IconButton onClick={handleCloseEditDialog} size="small">
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers>
                    <Grid container spacing={3}>
                        {competition?.isTeamCompetition ? (
                            <>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Team Name *"
                                        value={editForm.teamName}
                                        onChange={(e) => setEditForm(prev => ({ ...prev, teamName: e.target.value }))}
                                        placeholder="Enter team name"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Team Members
                                    </Typography>
                                </Grid>

                                {Array.from({ length: competition.teamSize }, (_, index) => (
                                    <Grid item xs={12} key={index}>
                                        <Autocomplete
                                            options={userSearchResults}
                                            getOptionLabel={(option) => `${option.fullName} (${option.email})`}
                                            value={userSearchResults.find(u => u.id === editForm.teamMembers[index]) || null}
                                            onChange={(e, value) => handleTeamMemberChange(index, value)}
                                            onInputChange={(e, value, reason) => searchUsers(value, reason)}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={`Member ${index + 1} *`}
                                                    placeholder="Search for a member"
                                                />
                                            )}
                                        />
                                    </Grid>
                                ))}
                            </>
                        ) : (
                            <Grid item xs={12}>
                                <Autocomplete
                                    options={userSearchResults}
                                    getOptionLabel={(option) => `${option.fullName} (${option.email})`}
                                    value={userSearchResults.find(u => u.id === editForm.userId) || null}
                                    onChange={(e, value) => {
                                        setEditForm(prev => ({ ...prev, userId: value?.id || null }));
                                        // ✅ Lisa valitud kasutaja selectedUsers'i
                                        if (value && !selectedUsers.find(u => u.id === value.id)) {
                                            setSelectedUsers([value]);
                                        }
                                    }}
                                    onInputChange={(e, value, reason) => searchUsers(value, reason)}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Participant *"
                                            placeholder="Search for a participant"
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Autocomplete
                                options={competition?.categories || []}
                                value={editForm.category}
                                onChange={(e, value) => setEditForm(prev => ({ ...prev, category: value }))}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Category *"
                                        required
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseEditDialog} disabled={editLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleUpdateRegistration}
                        disabled={editLoading}
                    >
                        {editLoading ? 'Updating...' : 'Update'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Video Player Dialog with Approval */}
            {canViewVideos && (
                <VideoDialog
                    open={!!selectedVideo}
                    onClose={() => {
                        setSelectedVideo(null);
                        setSelectedResultId(null);
                        setSelectedApproved(false);
                    }}
                    maxWidth="md"
                    fullWidth
                    fullScreen={isMobile}
                >
                    <DialogTitle>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="h6">Workout Video</Typography>
                                {selectedApproved && (
                                    <Chip
                                        icon={<CheckCircle />}
                                        label="Approved"
                                        color="success"
                                        size="small"
                                    />
                                )}
                            </Box>
                            <IconButton
                                onClick={() => {
                                    setSelectedVideo(null);
                                    setSelectedResultId(null);
                                    setSelectedApproved(false);
                                }}
                                size="small"
                            >
                                <Close />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        {selectedVideo && (
                            <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                                <iframe
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%'
                                    }}
                                    src={getYoutubeEmbedUrl(selectedVideo)}
                                    title="Workout Video"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant={selectedApproved ? "outlined" : "contained"}
                            color={selectedApproved ? "error" : "success"}
                            onClick={handleApproveToggle}
                            disabled={approving}
                            startIcon={selectedApproved ? null : <CheckCircle />}
                        >
                            {approving
                                ? 'Processing...'
                                : (selectedApproved ? 'Unapprove' : 'Approve')
                            }
                        </Button>
                    </DialogActions>
                </VideoDialog>
            )}
        </>
    );
}